import { GameKind } from "@pax/api-contract/common";
import { Casting, LiveBan, LiveInputKind } from "@pax/api-contract/internal/v1alpha1";
import { ModerationCategory, ModerationWarning } from "@pax/api-contract/moderation/v1alpha1";
import {
  CommittedDeltaSchema,
  IdSchema,
  IssueSchema,
  ProposedDeltaSchema,
  SkillSchema,
  UserDeltaSchema,
  UserProposalSchema,
} from "@pax/world";
import { z } from "zod";

export { GameKind };

export const EXAMPLES = {
  gameId: "9d2f6c1e-3b7a-4f0e-8c21-5a6d7e8f9a0b",
  userId: "0f3c1d7e-5a2b-4c8d-9e6f-1a2b3c4d5e6f",
  agentId: "5b7e2c4a-8d1f-4e3b-a6c9-0d2e4f6a8b1c",
  timestamp: "2026-09-04T17:00:00.000Z",
  token: "3d7c1f2a9b8e4c6d5f0a1b2c3d4e5f60718293a4b5c6d7e8f9a0b1c2d3e4f506",
} as const;

export const Timestamp = z.iso.datetime({ offset: true }).meta({
  id: "Timestamp",
  description: "An ISO 8601 timestamp with an explicit UTC offset.",
  example: EXAMPLES.timestamp,
});
export type Timestamp = z.infer<typeof Timestamp>;

export const GameId = z.uuid().brand<"GameId">().meta({
  id: "GameId",
  description: "The client-generated game id.",
  example: EXAMPLES.gameId,
});
export type GameId = z.infer<typeof GameId>;

export const GameIdParams = z
  .strictObject({
    id: GameId,
  })
  .meta({
    id: "GameIdParams",
    description: "A route parameter naming one game.",
    example: { id: EXAMPLES.gameId },
  });
export type GameIdParams = z.infer<typeof GameIdParams>;

export const Revision = z.number().int().nonnegative().meta({
  id: "Revision",
  description: "A global committed-delta revision.",
  example: 1400,
});
export type Revision = z.infer<typeof Revision>;

export const UserId = z.uuid().meta({
  description: "A Pax account id, which is also the member's identity inside the world.",
  example: EXAMPLES.userId,
});

export const NotifyPayersRequest = z.strictObject({
  signal: z.enum(["payment_required", "balance_changed"]).meta({
    description:
      "`payment_required`: Pax refused a call they pay for. `balance_changed`: a call they pay for was charged.",
  }),
  userIds: z.array(UserId).min(1).max(256),
});
export type NotifyPayersRequest = z.infer<typeof NotifyPayersRequest>;

/** One change to a skill, as a member whose roles allow `editRules` makes it. */
export const SkillEdit = z.discriminatedUnion("action", [
  z.strictObject({ action: z.literal("create"), skill: SkillSchema }),
  z.strictObject({
    action: z.literal("update"),
    skillId: IdSchema,
    changes: SkillSchema.omit({ id: true }).partial(),
    basedOnRevision: Revision.meta({
      description:
        "The revision the skill last changed at when the member began editing it. If it has changed since, the update is refused.",
    }),
  }),
  z.strictObject({ action: z.literal("delete"), skillId: IdSchema }),
]);
export type SkillEdit = z.infer<typeof SkillEdit>;

export const EditSkillRequest = z.strictObject({
  userId: UserId.meta({ description: "The member editing, whose roles must allow `editRules`." }),
  edit: SkillEdit,
});
export type EditSkillRequest = z.infer<typeof EditSkillRequest>;

export const SelectGameMasterRequest = z.strictObject({
  userId: UserId,
  agentVersionId: z.uuid().meta({ description: "The published agent version to run." }),
});
export type SelectGameMasterRequest = z.infer<typeof SelectGameMasterRequest>;

export const LobbyCommandResponse = z.strictObject({
  revision: Revision.meta({ description: "The revision the command committed through." }),
});
export type LobbyCommandResponse = z.infer<typeof LobbyCommandResponse>;

export const IdempotencyKey = z.uuid().meta({
  id: "IdempotencyKey",
  description:
    "Chosen by the client for each proposal. Sending a committed proposal's key again answers with the revision it committed through and commits nothing; a refused proposal is checked again.",
});

/**
 * Deltas committed together or not at all. A player sends `UserProposal`s; the game master sends
 * `ProposedDelta`s.
 */
export const ProposalSchema = z.strictObject({
  idempotencyKey: IdempotencyKey,
  deltas: z.array(z.union([ProposedDeltaSchema, UserProposalSchema])).min(1),
});
export type Proposal = z.infer<typeof ProposalSchema>;

/**
 * Why moderation kept something a member wrote out of the game: it redacted it, the member is banned
 * from live games, or moderation couldn't judge it, and nothing gets through unjudged. Nothing in
 * the proposal was committed or sent to anyone.
 */
export const ModerationRefusal = z.discriminatedUnion("reason", [
  z.strictObject({
    reason: z.literal("redacted"),
    kind: LiveInputKind,
    warning: ModerationWarning,
    categories: z.array(ModerationCategory).min(1).meta({
      description: "The categories that caused the redaction, most severe first.",
    }),
    nearBan: z.boolean().meta({ description: "Whether the member is close to a ban." }),
  }),
  z.strictObject({ reason: z.literal("banned"), ban: LiveBan }),
  z.strictObject({
    reason: z.literal("tooLong"),
    max: z
      .number()
      .int()
      .positive()
      .meta({ description: "The most characters one input may have." }),
  }),
  z.strictObject({ reason: z.literal("unavailable") }),
]);
export type ModerationRefusal = z.infer<typeof ModerationRefusal>;

export const ProposalResult = z.discriminatedUnion("status", [
  z.strictObject({
    status: z.literal("committed"),
    idempotencyKey: IdempotencyKey,
    throughRevision: Revision.meta({
      description: "The proposal is applied once the socket's head reaches this revision.",
    }),
  }),
  z.strictObject({
    status: z.literal("rejected"),
    idempotencyKey: IdempotencyKey,
    issues: z.array(IssueSchema).min(1),
    currentRevision: Revision.meta({
      description: "The revision the proposal was validated against.",
    }),
  }),
  z.strictObject({
    status: z.literal("refused"),
    idempotencyKey: IdempotencyKey,
    moderation: ModerationRefusal,
  }),
]);
export type ProposalResult = z.infer<typeof ProposalResult>;

/** A delta as a connection reads it: whole for the game master and graders, a player's own copy otherwise. */
export const RecordDeltaSchema = z.union([CommittedDeltaSchema, UserDeltaSchema]);
export type RecordDelta = z.infer<typeof RecordDeltaSchema>;

/** What the game master is doing about a subject; `done` says it has stopped, written or not. */
export const ProgressKindSchema = z.enum(["thinking", "tool", "proposal", "note", "done"]);
export type ProgressKind = z.infer<typeof ProgressKindSchema>;

/** What the game master is working on. Only the players who can read it receive its progress. */
export const ProgressSubjectSchema = z
  .discriminatedUnion("type", [
    z.strictObject({ type: z.literal("jump") }),
    z.strictObject({ type: z.literal("thread"), perspectiveHandle: IdSchema, threadId: IdSchema }),
    z.strictObject({ type: z.literal("advisor"), userId: UserId }),
    z.strictObject({ type: z.literal("perspectiveChange"), userId: UserId, requestId: IdSchema }),
  ])
  .meta({
    id: "ProgressSubject",
    description:
      "`jump`: the active jump, seen by every player. `thread`: a reply in one perspective's thread, seen by that perspective's players. `advisor`: an advisor answer, seen by that user alone. `perspectiveChange`: the answer to a user's ask to change perspective, seen by that user alone.",
    example: { type: "advisor", userId: EXAMPLES.userId },
  });
export type ProgressSubject = z.infer<typeof ProgressSubjectSchema>;

/** A member of a thread typing, or stopping, as the game master shows it to the players who can read it. */
export const TypingRequest = z
  .strictObject({
    perspectiveHandle: IdSchema,
    threadId: IdSchema,
    memberId: IdSchema,
    typing: z.boolean(),
  })
  .meta({
    id: "TypingRequest",
    example: {
      perspectiveHandle: "japan",
      threadId: "seoul",
      memberId: "south-korea",
      typing: true,
    },
  });
export type TypingRequest = z.infer<typeof TypingRequest>;

export const ProgressRequest = z.strictObject({
  subject: ProgressSubjectSchema,
  kind: ProgressKindSchema,
  label: z.string().min(1).max(2_000),
});
export type ProgressRequest = z.infer<typeof ProgressRequest>;

/** Who a player agent plays: a name for its account, and how it is told to play. */
export const PlayerPersona = z.strictObject({
  name: z.string().min(1).max(100),
  instructions: z.string().min(1).max(20_000),
});
export type PlayerPersona = z.infer<typeof PlayerPersona>;

/** One player of a graded game: their account, and how they were told to play. */
export const GradedPlayer = PlayerPersona.extend({ userId: z.uuid() });
export type GradedPlayer = z.infer<typeof GradedPlayer>;

/** One weighted part of the case's rubric. Its description carries the scoring anchors. */
export const RubricSection = z.strictObject({
  key: z.string().regex(/^[a-z][a-z0-9-]*$/),
  name: z.string().min(1).max(100),
  description: z.string().min(1).max(5_000),
  weight: z.number().positive(),
});
export type RubricSection = z.infer<typeof RubricSection>;

/**
 * What a grader is asked to grade: the case, its rubric, and every player. Who played which
 * perspective, and when, is in the record.
 */
export const GradingAssignment = z.strictObject({
  caseTitle: z.string().min(1).max(200),
  caseDescription: z.string().max(10_000),
  rubric: z.array(RubricSection).min(1).max(8),
  startingRevision: Revision.meta({ description: "The revision the case's game started from." }),
  players: z.array(GradedPlayer).min(1),
});
export type GradingAssignment = z.infer<typeof GradingAssignment>;

/**
 * What an agent the game starts for someone else is told to do, handed to its container when it
 * starts: a grader's assignment, or the persona a player agent plays.
 */
export const AgentBrief = z.discriminatedUnion("type", [
  z.strictObject({ type: z.literal("grader"), assignment: GradingAssignment }),
  z.strictObject({ type: z.literal("player"), persona: PlayerPersona }),
]);
export type AgentBrief = z.infer<typeof AgentBrief>;

export const StartGraderRequest = z.strictObject({
  agentId: z.uuid().meta({
    description: "The grader's agent id, chosen by the caller. Starting it again changes nothing.",
  }),
  agentVersionId: z.uuid().meta({ description: "The published grader version to run." }),
  assignment: GradingAssignment,
});
export type StartGraderRequest = z.infer<typeof StartGraderRequest>;

/** An agent the game started, or found already running for the request. */
export const StartedAgent = z.strictObject({
  agentId: z.uuid(),
});
export type StartedAgent = z.infer<typeof StartedAgent>;

/** Something the grader saw at one revision that its score rests on. */
export const ScorecardFinding = z.strictObject({
  revision: Revision.min(1),
  perspective: z.string().min(1).max(500).optional().meta({
    description: "The perspective's name when the finding is about what one perspective saw.",
  }),
  text: z.string().min(1).max(2_000),
});
export type ScorecardFinding = z.infer<typeof ScorecardFinding>;

/** One rubric section's score from 0 to 100, what it rests on, and a short summary. */
export const ScoredSection = z.strictObject({
  key: z.string().min(1).max(100),
  score: z.number().min(0).max(100),
  summary: z.string().min(1).max(5_000),
  findings: z.array(ScorecardFinding).max(20),
});
export type ScoredSection = z.infer<typeof ScoredSection>;

/** A grader's scorecard: every rubric section scored once, and a summary of the game. */
export const ScorecardSubmission = z.strictObject({
  summary: z.string().min(1).max(10_000),
  sections: z.array(ScoredSection).min(1).max(8),
});
export type ScorecardSubmission = z.infer<typeof ScorecardSubmission>;

export const StartPlayerAgentRequest = z.strictObject({
  userId: UserId.meta({ description: "The member whose account the agent plays." }),
  agentVersionId: z.uuid().meta({ description: "The published player agent version to run." }),
  persona: PlayerPersona,
});
export type StartPlayerAgentRequest = z.infer<typeof StartPlayerAgentRequest>;

export const PlayerConnection = z
  .strictObject({
    token: z.string().min(1).meta({
      description: "Send it in the first socket frame as `authenticate.token`.",
      example: EXAMPLES.token,
    }),
    expiresAt: Timestamp.meta({
      description: "When the game token stops working. Connect again after.",
    }),
    userId: UserId,
  })
  .meta({
    id: "PlayerConnection",
    description: "A short-lived player game token and the caller's world identity.",
  });
export type PlayerConnection = z.infer<typeof PlayerConnection>;

export const RunnerLifecycleEvent = z.discriminatedUnion("kind", [
  z.strictObject({
    kind: z.literal("agent_started"),
    agentId: z.uuid(),
    startedAt: Timestamp,
  }),
  z.strictObject({
    kind: z.literal("agent_stopped"),
    agentId: z.uuid(),
    stoppedAt: Timestamp,
    clean: z.boolean().meta({
      description: "The agent exited with code 0 and its state snapshot was uploaded.",
    }),
    requested: z.boolean().meta({ description: "The stop came from a `stop` call." }),
    exitCode: z.number().int().optional(),
    stderrTail: z.string().max(20_000).optional(),
  }),
]);
export type RunnerLifecycleEvent = z.infer<typeof RunnerLifecycleEvent>;

export const GameMasterState = z
  .enum(["unassigned", "stopped", "starting", "connected", "paused", "failed"])
  .meta({
    id: "GameMasterState",
    description:
      "`unassigned`: no agent holds the role. `stopped`: an agent holds it but is not running, as after 15 minutes with no player acting; the next player to connect or act starts it. `starting`: its container was started and its socket has not attached. `connected`: its socket is attached. `paused`: someone paused the game, and it runs again once someone resumes. `failed`: it failed three times in 30 minutes, and nothing starts it until a member who may change the game master selects one.",
    example: "connected",
  });
export type GameMasterState = z.infer<typeof GameMasterState>;

export const GameMasterFailure = z
  .object({
    reason: z.string().min(1).max(500).meta({
      description:
        "Why it failed: the last line the agent wrote to stderr as it exited, or what the session saw instead, such as a container that never connected.",
      example: '[pax-agent-sdk] ai.baseUrls: Unrecognized key: "alibaba"; exiting',
    }),
    failedAt: Timestamp,
  })
  .meta({ id: "GameMasterFailure" });
export type GameMasterFailure = z.infer<typeof GameMasterFailure>;

export const GameMasterStatus = z.object({
  state: GameMasterState,
  agentId: z.uuid().nullable(),
  agentVersionId: z.uuid().nullable(),
  failure: GameMasterFailure.nullable().meta({
    description:
      "While `starting`, the failure it is starting again after. While `failed`, the one the session gave up on. Null otherwise.",
  }),
});
export type GameMasterStatus = z.infer<typeof GameMasterStatus>;

export const GameSessionSummary = z.strictObject({
  gameId: GameId,
  headRevision: Revision,
  gameMaster: GameMasterStatus,
});
export type GameSessionSummary = z.infer<typeof GameSessionSummary>;

export const RefreshGameResponse = z.strictObject({
  headRevision: Revision.nullable().meta({
    description:
      "The record's latest revision once the session read the game, so nothing after it was committed under what the session read before. Null for a game whose session hasn't started, which is still its stored World artifact.",
  }),
});
export type RefreshGameResponse = z.infer<typeof RefreshGameResponse>;

export const ReportRunnerEventRequest = z.strictObject({
  event: RunnerLifecycleEvent,
});
export type ReportRunnerEventRequest = z.infer<typeof ReportRunnerEventRequest>;

export const ReportRunnerEventResponse = z.strictObject({
  recorded: z.boolean(),
});
export type ReportRunnerEventResponse = z.infer<typeof ReportRunnerEventResponse>;

export const ForkGameRequest = z.strictObject({
  gameId: GameId.meta({ description: "The new game's id." }),
  throughRevision: Revision.meta({
    description:
      "The last revision of this game the new game keeps. It may not fall inside a jump.",
  }),
  userId: UserId.meta({
    description:
      "The user forking, who stays on the perspective they were on at `throughRevision`.",
  }),
});
export type ForkGameRequest = z.infer<typeof ForkGameRequest>;

export const ForkGameResponse = z.strictObject({
  gameId: GameId,
  throughRevision: Revision,
  roundsCompleted: z
    .number()
    .int()
    .nonnegative()
    .meta({ description: "How many rounds the new game's World has completed." }),
  sha256: z
    .string()
    .regex(/^[a-f0-9]{64}$/)
    .meta({ description: "SHA-256 of the new game's World artifact." }),
  casting: Casting.meta({ description: "The forking user's perspective in the new game." }),
});
export type ForkGameResponse = z.infer<typeof ForkGameResponse>;

export const DeltaRecordQuery = z.strictObject({
  after: z.coerce.number().int().nonnegative().default(0),
});
export type DeltaRecordQuery = z.infer<typeof DeltaRecordQuery>;

export const DeltaRecordResponse = z.strictObject({
  scannedThroughRevision: Revision,
  items: z.array(RecordDeltaSchema),
});
export type DeltaRecordResponse = z.infer<typeof DeltaRecordResponse>;
